import { Card, CardContent, Typography, IconButton, UserFlag, Box, CloseIcon, Loader,
    UserSocialMediaLinks, Badge, UserAvatar, ExplorerPlusIcon } from 'src/components';
import { useIsMobile } from 'src/hooks';
import { useState, useEffect } from 'react';
import { getUser } from 'src/utils';
import { useFeatureTravellerStore } from 'src/stores';
import { formatDistanceToNowStrict } from "date-fns";

export default function UserPopupCard({ onClick, onClose }) {
    const [loading, setLoading] = useState(false);

    const selectedTraveller = useFeatureTravellerStore((state) => state.selectedTraveller);
    const setSelectedTraveller = useFeatureTravellerStore((state) => state.setSelectedTraveller);
    const loadedTravellerData = useFeatureTravellerStore((s) => s.loadedTravellerData);
    const setLoadedTravellerData = useFeatureTravellerStore((s) => s.setLoadedTravellerData);
    
    const isMobile = useIsMobile();

    useEffect(() => {
        if (!selectedTraveller?.id || selectedTraveller?.countryIsoAlpha2) return;

        const fetchUser = async () => {
            try {
                setLoading(true);
                const res = await getUser(selectedTraveller.id);
                setLoadedTravellerData({ [res.id]: res });
                setSelectedTraveller(res);
            } catch (err) {
                console.error("Error fetching user details", err);
            } finally {
                setLoading(false);
            }
        };

        if (!loadedTravellerData[selectedTraveller.id]) {
            fetchUser();
        }
    }, [selectedTraveller, loadedTravellerData]);

    if (!selectedTraveller) return null;

    const fullUser = loadedTravellerData[selectedTraveller?.id] || selectedTraveller;

    const bioPreview = fullUser?.bio?.slice(0, 77) + (fullUser?.bio?.length > 77 ? '...' : '');

    const cardStyles = {
        width: isMobile ? '90%' : 340,
        minHeight: 100,
        display: 'flex',
        flexDirection: 'column',
        position: 'fixed',
        bottom: isMobile ? 'calc(75px + var(--safe-area-bottom))' : 50,
        left: isMobile ? '5%' : 50,
        zIndex: 1000,
        borderRadius: 2,
        boxShadow: 6,
        backgroundColor: 'white',
        overflow: 'hidden',
        cursor: 'pointer'
    };

    return (
        <Card sx={cardStyles} onClick={() => onClick()}>
            {loading || !fullUser?.name ? (
                <Loader />
            ):
                <CardContent sx={{ padding: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>

                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between'}}>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {fullUser?.countryIsoAlpha2 ? (
                                <Badge
                                    overlap="circular"
                                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                    badgeContent={
                                        <UserFlag countryCode={fullUser.countryIsoAlpha2} />
                                    }>
                                    <UserAvatar user={fullUser} size={48}/>
                                </Badge>
                            ) : (
                                <UserAvatar user={fullUser} size={48}/>
                            )}
                            <Typography variant="h5" fontWeight="bold">
                                {fullUser?.name}
                            </Typography>
                        </Box>

                        <Box sx={{display: 'flex', justifyContent: 'row', alignItems: 'center'}}>
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{
                                    fontWeight: 500,
                                    whiteSpace: 'nowrap',
                                    opacity: 0.8
                                }}
                            >
                                {formatLastActive(fullUser?.lastActiveAt)}
                            </Typography>
                            {fullUser?.membership !== "Free" && (                            
                                <ExplorerPlusIcon 
                                    tier={fullUser?.membership?.toLowerCase()}
                                    size={22}
                                    variant="animated"
                                />
                            )}

                            <IconButton disableRipple onClick={(e) => {
                                e.stopPropagation();
                                onClose();
                            }}>
                                <CloseIcon fontSize="small" />
                            </IconButton>
                        </Box>

                    </Box>

                    {/* Bio */}
                    {fullUser?.bio && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            {bioPreview}
                        </Typography>
                    )}

                    <Box sx={{display: 'flex', flexDirection: 'row', justifyContent: 'space-between'}}>
                        <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', mt: 1 }}>
                            <Typography variant="body2" fontWeight="bold" sx={{ mr: 1 }}>
                                {fullUser?.trips?.length === 0 && 'No Trips Tracked'}
                                {fullUser?.trips?.length === 1 && '1 Trip Tracked'}
                                {fullUser?.trips?.length > 1 && `${fullUser.trips.length} Trips Tracked`}
                            </Typography>
                        </Box>

                        {/* Social Media */}
                        <Box sx={{display: 'flex', flexDirection: 'row', alignItems: 'center', mt: 1}}>
                            <Typography variant="body2" fontWeight="bold" sx={{mr: 1}}>
                                Social Media:
                            </Typography>
                            
                            <UserSocialMediaLinks ig={fullUser?.instagramHandle} links={fullUser?.socialMediaLinks} />
                        </Box>
                    </Box>

                </CardContent>
            }
        </Card>
    );
}

function formatLastActive(timestamp) {
    if (!timestamp) return 'a while ago';

    const distance = formatDistanceToNowStrict(new Date(timestamp), { addSuffix: false });

    const daysMatch = distance.match(/(\d+) days?/);
    const monthsMatch = distance.match(/(\d+) months?/);

    if (daysMatch === null && monthsMatch === null) {
        return '1d ago';
    }

    if (daysMatch) {
        const days = parseInt(daysMatch[1]);
        if (days <= 13) {
            return `${days}d ago`;
        }
        if (days <= 28) {
            const weeks = Math.floor(days / 7);
            return `${weeks}w ago`;
        }
    }

    // Over ~4 weeks
    return 'a while ago';
}